import React, { forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    getNextHighlightedIndexOnArrowKey,
    useDropdownPosition,
    useOutsideClick,
    useScrollHighlightedIntoView,
} from "../../../../hooks/useDropdownBehavior";
import {
    StyledFilterChevron,
    StyledFilterItem,
    StyledFilterList,
    StyledFilterStateItem,
    StyledFilterTrigger,
    StyledFilterWrapper,
} from "./inputs.styles";

const TYPEAHEAD_RESET_MS = 500;

/**
 * DataTable toolbar single-select filter: same trigger as `DataTableMultiSelectFilter`, black while
 * an option is chosen. Re-picking the selected option clears it.
 * @param {Array<{value, label, disabled?, disabledReason?}>} [props.options] Selectable options.
 * @param {Function} [props.renderLabel] Renders an option's label (see `../labels/`); defaults to `option.label`.
 * @param {string|number} [props.value] Controlled selected value; `""` means none.
 * @param {Function} [props.onChange] Called with `{ target: { value, name }, selectedOption }`.
 * Also: placeholder, onOpenChange, disabled, disabledReason, loading, loadingMessage, emptyMessage, name, id, ariaLabel, className (self-explanatory).
 */
const DataTableSingleSelectFilter = forwardRef(function DataTableSingleSelectFilter(
    {
        options = [],
        renderLabel = (option) => option.label,
        placeholder = "Filter",
        value = "",
        onChange,
        onOpenChange,
        disabled = false,
        disabledReason,
        loading = false,
        loadingMessage = "Loading options...",
        emptyMessage = "No options available",
        name = "",
        id,
        ariaLabel,
        className,
    },
    ref
) {
    const [isOpen, setIsOpen] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const containerRef = useRef(null);
    const listRef = useRef(null);
    const typeaheadRef = useRef({ search: "", timeoutId: null });
    const hasValue = value !== "" && value !== null && value !== undefined;
    const selectedOption = hasValue ? options.find((option) => String(option.value) === String(value)) : undefined;
    const listboxId = id ? `${id}-listbox` : undefined;

    const setOpen = (nextOpen) => {
        setIsOpen(nextOpen);
        setHighlightedIndex(-1);
        if (onOpenChange) {
            onOpenChange(nextOpen);
        }
    };

    const handleSelect = (option) => {
        if (disabled || option.disabled) {
            return;
        }
        const isCurrent = selectedOption && String(selectedOption.value) === String(option.value);
        const target = { value: isCurrent ? "" : option.value, name };
        if (onChange) {
            onChange({ target, currentTarget: target, selectedOption: isCurrent ? null : option });
        }
        setOpen(false);
    };

    const findTypeaheadMatch = (character) => {
        const buffer = typeaheadRef.current;
        clearTimeout(buffer.timeoutId);
        buffer.search += character.toLowerCase();
        buffer.timeoutId = setTimeout(() => {
            buffer.search = "";
        }, TYPEAHEAD_RESET_MS);

        for (let count = 1; count <= options.length; count += 1) {
            const index = (highlightedIndex + count + options.length) % options.length;
            const option = options[index];
            if (option && !option.disabled && String(option.label || "").toLowerCase().startsWith(buffer.search)) {
                return index;
            }
        }
        return -1;
    };

    const handleKeyDown = (event) => {
        if (disabled) {
            return;
        }

        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            if (isOpen && highlightedIndex >= 0 && options[highlightedIndex]) {
                handleSelect(options[highlightedIndex]);
            } else {
                setOpen(!isOpen);
            }
            return;
        }

        if (event.key === "Escape") {
            setOpen(false);
            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            if (!isOpen) {
                setOpen(true);
            }
            setHighlightedIndex((current) => getNextHighlightedIndexOnArrowKey(options, current, event.key));
            return;
        }

        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            const matchIndex = findTypeaheadMatch(event.key);
            if (matchIndex >= 0) {
                event.preventDefault();
                if (!isOpen) {
                    setOpen(true);
                }
                setHighlightedIndex(matchIndex);
            }
        }
    };

    useOutsideClick(containerRef, listRef, isOpen, () => setOpen(false));

    const menuPosition = useDropdownPosition(containerRef, isOpen);

    useScrollHighlightedIntoView(listRef, isOpen, highlightedIndex);

    useEffect(() => {
        setHighlightedIndex((current) => (current >= 0 && current < options.length ? current : -1));
    }, [options]);

    useEffect(() => () => clearTimeout(typeaheadRef.current.timeoutId), []);

    return (
        <StyledFilterWrapper ref={containerRef} className={className}>
            <StyledFilterTrigger
                ref={ref}
                type="button"
                id={id}
                $active={Boolean(selectedOption)}
                $open={isOpen}
                $disabled={disabled}
                disabled={disabled}
                role="combobox"
                aria-label={ariaLabel || placeholder}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listboxId}
                title={disabled ? disabledReason : undefined}
                onClick={() => !disabled && setOpen(!isOpen)}
                onKeyDown={handleKeyDown}
            >
                <span>{placeholder}</span>
                <StyledFilterChevron $open={isOpen} className="fal fa-chevron-down" aria-hidden="true" />
            </StyledFilterTrigger>

            {isOpen && createPortal(
                <StyledFilterList
                    id={listboxId}
                    ref={listRef}
                    role="listbox"
                    $top={menuPosition.top}
                    $left={menuPosition.left}
                    $width={menuPosition.width}
                >
                    {loading ? (
                        <StyledFilterStateItem role="status">{loadingMessage}</StyledFilterStateItem>
                    ) : options.length === 0 ? (
                        <StyledFilterStateItem>{emptyMessage}</StyledFilterStateItem>
                    ) : (
                        options.map((option, index) => {
                            const selected = Boolean(selectedOption) && String(selectedOption.value) === String(option.value);
                            return (
                                <StyledFilterItem
                                    key={option.value}
                                    role="option"
                                    aria-selected={selected}
                                    aria-disabled={option.disabled || undefined}
                                    $selected={selected}
                                    $highlighted={index === highlightedIndex}
                                    $disabled={Boolean(option.disabled)}
                                    title={option.disabled ? option.disabledReason : option.title}
                                    onClick={() => handleSelect(option)}
                                    onMouseEnter={() => !option.disabled && setHighlightedIndex(index)}
                                >
                                    {renderLabel(option)}
                                </StyledFilterItem>
                            );
                        })
                    )}
                </StyledFilterList>,
                document.body
            )}
        </StyledFilterWrapper>
    );
});

DataTableSingleSelectFilter.displayName = "DataTableSingleSelectFilter";

export default DataTableSingleSelectFilter;
